import React, { createContext, useContext, useState, useCallback } from 'react';

import type { FeatureKey, Tier } from "@/contexts/AccessContext";

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info';
  message: string;
}

interface UIContextType {
  isLoading: boolean;
  setLoading: (loading: boolean) => void;
  showToast: (type: Toast['type'], message: string) => void;
  toasts: Toast[];
  removeToast: (id: string) => void;
  isDarkMode: boolean;
  toggleTheme: () => void;

  isUpgradeModalOpen: boolean;
  upgradeModal: {
    feature?: FeatureKey;
    reason?: "login_required" | "upgrade_required" | "limit_reached";
    requiredTier?: Tier;
    limit?: number;
    current?: number;
  } | null;
  openUpgradeModal: (payload: {
    feature?: FeatureKey;
    reason?: "login_required" | "upgrade_required" | "limit_reached";
    requiredTier?: Tier;
    limit?: number;
    current?: number;
  }) => void;
  closeUpgradeModal: () => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [upgradeModal, setUpgradeModal] = useState<UIContextType["upgradeModal"]>(null);
  const isUpgradeModalOpen = Boolean(upgradeModal);

  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
        (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 5000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  }, []);

  const openUpgradeModal = useCallback<UIContextType["openUpgradeModal"]>((payload) => {
    setUpgradeModal(payload);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModal(null);
  }, []);

  return (
    <UIContext.Provider value={{
      isLoading,
      setLoading,
      showToast,
      toasts,
      removeToast,
      isDarkMode,
      toggleTheme,

      isUpgradeModalOpen,
      upgradeModal,
      openUpgradeModal,
      closeUpgradeModal,
    }}>
      {children}
    </UIContext.Provider>
  );
};

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error('useUI must be used within a UIProvider');
  }
  return context;
}; 